/**
 * Pre-consent telemetry, shared by app.bookeper.pl (Next.js) and www.bookeper.pl (Vite landing)
 * (BOO-5163). One owner flag, default OFF:
 *
 *   NEXT_PUBLIC_TELEMETRY_PRECONSENT_FULL  (apps/frontend)
 *   VITE_TELEMETRY_PRECONSENT_FULL         (apps/landing)
 *
 *   - unset / anything else: off. Nothing in this module changes behaviour.
 *   - "true" or "pending": record (masked, memory-only, anonymous) visitors who have NOT chosen yet.
 *   - "all" (a former value that also recorded decliners) reads as "pending".
 * Only a visitor who has not chosen is ever recorded (owner decision, #9200 Astra r1): an explicit
 * refusal stops the recording in every mode, and a visitor who accepted is recorded by the main
 * instance instead.
 *
 * Pure helpers only (no posthog-js import) so both apps and their tests share one definition of who
 * is recorded and what is masked.
 */
import { refusalKeepableNow } from './consent';

export type PreconsentTelemetryMode = 'off' | 'pending';

export type PreconsentConsentStatus = 'accepted' | 'declined' | null | undefined;

export function parsePreconsentTelemetryMode(
  value: string | null | undefined
): PreconsentTelemetryMode {
  const normalized = value?.trim().toLowerCase();
  return normalized === 'true' || normalized === 'pending' || normalized === 'all'
    ? 'pending'
    : 'off';
}

/** Routes that are never recorded: OAuth / signing callbacks carry one-time codes in the URL. */
const NEVER_RECORD_PREFIXES = ['/auth/callback', '/signing/callback', '/auth/auth-code-error'];

function stripLocale(pathname: string): string {
  return pathname.replace(/^\/(?:pl|en|uk)(?=\/|$)/, '') || '/';
}

/**
 * Whether a pre-consent instance may record this visitor on this path: only one who has not chosen.
 * An explicit refusal is never recorded, in any mode, and neither is a Do Not Track visitor: that rule lives HERE, so
 * every reconcile takes its STOP path for them (a sticky stop stays set, a running instance halts, a waiting `loaded`
 * callback does nothing) instead of a start that quietly returns (#9200 Cursor ydhBz).
 */
export function shouldRecordPreconsent(
  mode: PreconsentTelemetryMode,
  consent: PreconsentConsentStatus,
  pathname: string | null | undefined
): boolean {
  if (mode === 'off' || consent === 'accepted' || consent === 'declined') return false;
  if (isDoNotTrack()) return false;
  const route = stripLocale(pathname ?? '/');
  return !NEVER_RECORD_PREFIXES.some(
    (prefix) => route === prefix || route.startsWith(`${prefix}/`)
  );
}

/**
 * Anything that may hold a value the visitor typed, or a name/amount derived from one, stays masked
 * even on a public marketing page.
 */
export const PRECONSENT_ALWAYS_MASKED_SELECTOR = [
  '[data-ph-mask]',
  '.ph-no-capture',
  '.ph-mask',
  'form',
  'input',
  'textarea',
  'select',
  '[contenteditable]',
  '[role="textbox"]',
  '[role="dialog"]',
  '[role="alert"]',
  '.chat-message-content',
  '.tool-output-content',
  '.artifact-content',
].join(', ');

/**
 * Public, static, marketing or reference content: the same text for every visitor. The calculators
 * and the analyzer (`/kalkulatory`, `/analyzer`) are NOT in this list: their results derive from what
 * the visitor typed.
 */
const PUBLIC_CONTENT_PREFIXES = [
  '/blog',
  '/zawody',
  '/koszty',
  '/regulamin',
  '/polityka-prywatnosci',
];

/**
 * Whether a path of app.bookeper.pl is public static content, as opposed to an auth, app or form
 * surface. `/` is NOT public there (it is the signed-in app home); the landing, which is all
 * marketing, passes its own predicate to createPreconsentMaskTextFn.
 */
export function isPublicContentPath(pathname: string | null | undefined): boolean {
  const route = stripLocale(pathname ?? '/');
  return PUBLIC_CONTENT_PREFIXES.some(
    (prefix) => route === prefix || route.startsWith(`${prefix}/`)
  );
}

interface ClosestCapable {
  closest?: (selector: string) => unknown;
}

/** Replace every non-space character, keeping the text's rough length so layout stays readable. */
export function maskReplayText(text: string): string {
  return text.replace(/\S/g, '*');
}

/** Inside a form or a protected element, or not inspectable (then it counts as protected). */
function insideProtectedElement(element: unknown): boolean {
  const closest = (element as ClosestCapable | undefined)?.closest;
  if (typeof closest !== 'function') return true;
  try {
    return Boolean(closest.call(element, PRECONSENT_ALWAYS_MASKED_SELECTOR));
  } catch {
    return true;
  }
}

/**
 * Text masking for the pre-consent replay: EVERYTHING is masked (`maskTextSelector: '*'`) and this
 * function hands the original text back only for static public content outside forms, decided
 * at snapshot time from the live pathname so a single-page navigation from a blog post into the app
 * is masked again. The landing (all marketing) passes its own `isPublicPath`.
 */
export function createPreconsentMaskTextFn(
  getPathname: () => string,
  isPublicPath: (pathname: string) => boolean = isPublicContentPath
): (text: string, element?: unknown) => string {
  return (text, element) =>
    isPublicPath(getPathname()) && !insideProtectedElement(element) ? text : maskReplayText(text);
}

/**
 * Attribute values that are page structure or styling, never something the visitor typed or a
 * record the app loaded, so a masked replay still renders. rrweb passes its own inlined stylesheets
 * (`_cssText`) and layout values (`rr_*`) through the same function. Names are lower-cased.
 */
const STRUCTURAL_ATTRIBUTES = new Set(
  [
    // Markup and accessibility state.
    'class type role rel dir lang tabindex hidden disabled readonly required multiple open colspan',
    'rowspan media loading decoding crossorigin charset aria-hidden aria-expanded aria-selected',
    'aria-checked aria-disabled aria-current aria-pressed aria-modal aria-haspopup aria-orientation',
    'aria-busy aria-invalid aria-live',
    // Radix / shadcn state, which the Tailwind `data-[state=...]` variants style.
    'data-state data-side data-align data-orientation data-disabled data-highlighted data-slot',
    // SVG geometry (icons, chart shapes).
    'xmlns viewbox preserveaspectratio d points transform width height x y x1 y1 x2 y2 cx cy r rx',
    'ry offset opacity fill fill-rule fill-opacity clip-rule stroke stroke-width stroke-linecap',
    'stroke-linejoin stroke-dasharray stroke-dashoffset stroke-opacity stop-color',
    // rrweb's own.
    '_csstext rr_width rr_height rr_left rr_top rr_position rr_transform rr_display',
    'rr_scrollleft rr_scrolltop rr_mediastate rr_open_mode',
  ]
    .join(' ')
    .split(' ')
);

/** Hashed build output: the same file for every visitor. */
const STATIC_BUILD_ASSET = /^(?:https?:\/\/[^/?#]+)?\/_next\/static\//;

function isStructuralAttribute(name: string, value: string): boolean {
  const key = name.toLowerCase();
  if (key === 'style') return !/url\s*\(/i.test(value);
  if (key === 'href' || key === 'src') return STATIC_BUILD_ASSET.test(value);
  return STRUCTURAL_ATTRIBUTES.has(key);
}

/**
 * Attribute masking for the pre-consent replay, the counterpart of createPreconsentMaskTextFn:
 * text masking never sees attributes, and `title`, `alt`, `aria-label`, `href`, `src`, `srcset`,
 * `id`, `name`, `placeholder` or a `data-*` value can carry a client name, a NIP, a record id, a
 * one-time code or a signed storage URL. Where the text rule would mask the element's text, every
 * value is masked except the structural ones above.
 */
export function createPreconsentMaskAttributeFn(
  getPathname: () => string,
  isPublicPath: (pathname: string) => boolean = isPublicContentPath
): (name: string, value: string, element?: unknown) => string {
  return (name, value, element) => {
    if (isPublicPath(getPathname()) && !insideProtectedElement(element)) return value;
    return isStructuralAttribute(name, value) ? value : maskReplayText(value);
  };
}

/**
 * The session-recording options that are identical on both surfaces. The caller adds its own
 * `maskCapturedNetworkRequestFn` (URL scrubbing is app specific), `maskTextFn` and `maskAttributeFn`.
 */
export const PRECONSENT_SESSION_RECORDING_BASE = {
  maskAllInputs: true,
  maskInputOptions: { password: true },
  // `*` plus `maskTextFn`: nothing readable unless createPreconsentMaskTextFn hands it back.
  // Attributes are the caller's `maskAttributeFn` (createPreconsentMaskAttributeFn).
  maskTextSelector: '*',
  // No request/response headers or bodies, ever.
  recordHeaders: false,
  recordBody: false,
  // SDK-side 100% sampling. posthog-js 1.406.2 lets `session_recording.sampleRate` override the
  // project's remote sample rate.
  sampleRate: 1,
} as const;

/** What the pre-consent instance sends besides `$snapshot`: nothing. */
export const PRECONSENT_REPLAY_ONLY_EVENT = '$snapshot';

/**
 * Explicit, SDK-side capture settings for the MAIN instance when the flag is on. Before this the
 * main instance relied on the PostHog project's remote config for each of these. Pre-consent they
 * are cookieless (no identifier is stored), exactly as the pageviews already are.
 */
export const PRECONSENT_MAIN_CAPTURE_OPTIONS = {
  autocapture: true,
  rageclick: true,
  capture_heatmaps: true,
  capture_dead_clicks: true,
  disable_scroll_properties: false,
  capture_pageleave: true,
  capture_performance: { web_vitals: true, network_timing: true },
} as const;

/**
 * Who may be captured, decided in ONE place for every path (#9200 Astra r2). `cookieless_mode:
 * "on_reject"` still captures a decliner and a Do Not Track visitor anonymously, so a signal this
 * flag adds is not covered by the SDK's own consent handling and must be withheld here.
 */
const YES_LIKE = new Set(['1', 'yes', 'true']);

type DoNotTrackSource = {
  doNotTrack?: unknown;
  msDoNotTrack?: unknown;
  globalPrivacyControl?: unknown;
};

/** The signals the SDK's own consent check reads (`navigator.doNotTrack`, Global Privacy Control). */
export function isDoNotTrack(
  source: DoNotTrackSource = typeof navigator === 'undefined'
    ? {}
    : (navigator as unknown as DoNotTrackSource)
): boolean {
  return [source.doNotTrack, source.msDoNotTrack, source.globalPrivacyControl].some(
    (value) => value === true || (typeof value === 'string' && YES_LIKE.has(value.toLowerCase()))
  );
}

/** Telemetry that is not consent-gated by the SDK (auth form events): never a decliner, never DNT. */
export function mayCaptureVisitor(consent: PreconsentConsentStatus, doNotTrack: boolean): boolean {
  return mayAdmitUndecided(consent, doNotTrack);
}

/**
 * THE admission rule for a visitor who has not chosen (#9200 Astra r16). An undecided reading is evidence of "no choice" only
 * where this origin can keep a refusal: with the cookie, localStorage and sessionStorage all dropping writes, a decline
 * could not be retained and the next page reads null. There it may be a forgotten decline, so it gets nothing. Every
 * predicate below asks this one function; a decliner never passes and Do Not Track never passes. An accepted visitor
 * is not asked about storage (the acceptance was read back from the cookie).
 */
function mayAdmitUndecided(consent: PreconsentConsentStatus, doNotTrack: boolean): boolean {
  if (consent === 'declined' || doNotTrack) return false;
  return consent === 'accepted' || refusalKeepableNow();
}

/**
 * Signals this flag adds to the main instance (heatmaps, dead and rage clicks, scroll depth, web vitals): a visitor who
 * accepted, or who has NOT chosen yet (the point of the flag: cookieless, before the choice), and no Do Not Track. Never
 * a decliner, and never a consent record that cannot be read (the owner reads that as a refusal). #9200 Sentry
 * PRRT_kwDOO3Nfh86yaNMc: requiring an acceptance withheld them from exactly the visitors the flag is for.
 */
export function mayCaptureAddedSignals(
  consent: PreconsentConsentStatus,
  doNotTrack: boolean
): boolean {
  return mayAdmitUndecided(consent, doNotTrack);
}

/**
 * A replay snapshot leaves only for a visitor who has not chosen and has no Do Not Track. Asked at send
 * time by every replay instance: a decision made in another tab can reach a running recorder late.
 */
export function maySendReplaySnapshot(
  consent: PreconsentConsentStatus,
  doNotTrack: boolean
): boolean {
  return consent !== 'accepted' && mayCaptureVisitor(consent, doNotTrack);
}

/**
 * Whether a snapshot may LEAVE the page at this moment, asked at the network layer: a visitor who
 * refused (or whose decision cannot be read, which `getConsent` reports as a refusal) never; an accepted
 * visitor always (the main instance records from then on); everyone else unless Do Not Track.
 */
export function snapshotMayLeave(consent: PreconsentConsentStatus, doNotTrack: boolean): boolean {
  if (consent === 'accepted') return true;
  return mayAdmitUndecided(consent, doNotTrack);
}

export interface SnapshotNetworkGateOptions {
  /**
   * Extra ingest hosts, absolute or relative to the page. Not enough on its own: the SDK rewrites some
   * (`eu.posthog.com` is sent to `eu.i.posthog.com`), so every instance is also registered with
   * `registerGateInstance`, which reads the destinations the SDK actually resolved.
   */
  apiHosts?: ReadonlyArray<string | undefined>;
  /** Asked at send time; an error counts as "no". */
  mayLeave: () => boolean;
}

interface GateState {
  prefixes: { origin: string; prefix: string }[];
  mayLeave: () => boolean;
}

const GATE_KEY = '__bookeperConsentGate';
const OPEN_URL = Symbol('bookeperConsentGateUrl');
const WRAPPED = Symbol('bookeperConsentGateWrapped');

function gateState(): GateState {
  const scope = globalThis as typeof globalThis & { [GATE_KEY]?: GateState };
  return (scope[GATE_KEY] ??= { prefixes: [], mayLeave: () => false });
}

function pageBase(): string {
  const href = (globalThis as { location?: { href?: string } }).location?.href;
  return href ?? 'http://localhost/';
}

function resolveUrl(raw: unknown): URL | null {
  try {
    return new URL(String(raw), pageBase());
  } catch {
    return null;
  }
}

/** Register the ingest location `url` belongs to: its origin and the path it is served under. */
function addIngestLocation(url: URL, endpoint: boolean): void {
  const path = endpoint ? url.pathname.replace(/[^/]*\/?$/, '') : url.pathname;
  const prefix = `${path.replace(/\/+$/, '')}/`;
  const state = gateState();
  if (!state.prefixes.some((p) => p.origin === url.origin && p.prefix === prefix)) {
    state.prefixes.push({ origin: url.origin, prefix });
  }
}

function isIngestRequest(url: URL): boolean {
  return gateState().prefixes.some(
    ({ origin, prefix }) => url.origin === origin && url.pathname.startsWith(prefix)
  );
}

/**
 * What the gate holds: a snapshot (`/s/`), and a RETRY of any ingest request (`retry_count`), which the SDK
 * resends later without asking about consent again. Events are not held here: the main instances send each
 * event inside `capture` (no batching, no async compression), where the consent check is made.
 */
function blocked(raw: unknown): boolean {
  const url = resolveUrl(raw);
  if (!url || !isIngestRequest(url)) return false;
  const segments = url.pathname.split('/').filter(Boolean);
  const held = segments[segments.length - 1] === 's' || url.searchParams.has('retry_count');
  if (!held) return false;
  try {
    return !gateState().mayLeave();
  } catch {
    return true;
  }
}

type Patchable = Record<PropertyKey, unknown>;

function patchOnce(target: Patchable, name: string, wrap: (original: never) => unknown): void {
  const original = target[name];
  if (typeof original !== 'function' || (original as unknown as Patchable)[WRAPPED]) return;
  const wrapper = wrap(original as never) as Patchable;
  wrapper[WRAPPED] = true;
  try {
    target[name] = wrapper;
  } catch {
    // A frozen target cannot be wrapped; the SDK-side halt stays as the second line.
  }
}

/** The part of a posthog-js instance the gate reads: the destinations its request router resolved. */
export interface RoutedInstance {
  /** The SDK's request router (`endpointFor(target, path)`), typed loosely so any SDK version fits. */
  requestRouter?: unknown;
  config?: { api_host?: string };
}

/**
 * Register the destinations the SDK ACTUALLY sends to (#9200 Astra r7): its router rewrites the configured
 * host (`eu.posthog.com` becomes `eu.i.posthog.com` for events and snapshots, the flags host stays), so the
 * raw config string is never enough. Call right after `posthog.init`, before any request can be made.
 */
export function registerGateInstance(instance: RoutedInstance | null | undefined): void {
  if (!instance) return;
  const configured = resolveUrl(instance.config?.api_host);
  if (instance.config?.api_host && configured) addIngestLocation(configured, false);
  for (const [target, path] of [
    ['api', '/e/'],
    ['api', '/s/'],
    ['flags', '/flags/'],
  ] as const) {
    try {
      const router = instance.requestRouter as
        { endpointFor?: (target: string, path?: string) => string } | undefined;
      const raw = router?.endpointFor?.(target, path);
      const url = raw ? resolveUrl(raw) : null;
      if (url) addIngestLocation(url, true);
    } catch {
      // A router that cannot answer leaves the configured host as the fallback.
    }
  }
}

/**
 * The guarantee behind a refusal (#9200 Astra r6, r7). Switching the SDK off (`__loaded`, dispose) cannot
 * recall a request that is already past the SDK's checks: a snapshot in native async compression, a
 * retry held after a 503. Every byte goes through one of three transports, so they are wrapped and each
 * held request is checked against the CURRENT consent at the moment it is sent, and dropped (never sent)
 * when it may not leave. Every instance pins `api_transport: "XHR"`, whose prototype methods are patched at
 * call time, so it does not matter when the SDK captured its transport references; `fetch` is wrapped too
 * for an SDK loaded after this call. Idempotent; call before the instances are created and register each
 * one with `registerGateInstance`.
 */
export function installSnapshotNetworkGate(options: SnapshotNetworkGateOptions): void {
  const state = gateState();
  state.mayLeave = options.mayLeave;
  for (const host of options.apiHosts ?? []) {
    if (!host) continue;
    const url = resolveUrl(host);
    if (url) addIngestLocation(url, false);
  }

  const xhr = (globalThis as unknown as { XMLHttpRequest?: { prototype: Patchable } })
    .XMLHttpRequest;
  if (xhr?.prototype) {
    patchOnce(
      xhr.prototype,
      'open',
      (open: (...args: unknown[]) => unknown) =>
        function (this: Patchable, ...args: unknown[]) {
          this[OPEN_URL] = args[1];
          return open.apply(this, args);
        }
    );
    patchOnce(
      xhr.prototype,
      'send',
      (send: (...args: unknown[]) => unknown) =>
        function (this: Patchable, ...args: unknown[]) {
          // Dropped: the request is never sent, and no callback fires, so the SDK has nothing to retry.
          if (blocked(this[OPEN_URL])) return undefined;
          return send.apply(this, args);
        }
    );
  }

  if (typeof navigator !== 'undefined') {
    patchOnce(
      navigator as unknown as Patchable,
      'sendBeacon',
      (beacon: (...args: unknown[]) => unknown) =>
        function (this: unknown, ...args: unknown[]) {
          // "Queued" from the caller's view, so nothing is retried.
          if (blocked(args[0])) return true;
          return beacon.apply(navigator, args);
        }
    );
  }

  const scope = globalThis as unknown as Patchable;
  patchOnce(
    scope,
    'fetch',
    (original: (...args: unknown[]) => unknown) =>
      function (this: unknown, ...args: unknown[]) {
        const input = args[0] as { url?: string } | string | URL;
        const target =
          typeof input === 'object' && input !== null && 'url' in input ? input.url : input;
        if (blocked(target)) return Promise.resolve(new Response('{}', { status: 200 }));
        return original.apply(globalThis, args);
      }
  );
}

/** The part of a posthog-js instance a replay stop needs (structural, so this module stays SDK-free). */
export interface ReplayRecorderHandle {
  /** The SDK's own dispatch gate: while false, every send (queued, retried or beacon) is dropped. */
  __loaded?: boolean;
  stopSessionRecording(): void;
  sessionRecording?: { dispose?: (options?: { discardBufferedEvents?: boolean }) => void };
}

/**
 * Stop a pre-consent recorder. For a visitor who refused (or has Do Not Track) the recorder's buffered
 * events are discarded with the SDK's own `dispose({ discardBufferedEvents })`, so nothing queued
 * before the refusal is sent on unload (#9200 Astra r4). The instances also run with
 * `request_batching: false`, so no request waits in the SDK's queue either. A dispose is final for
 * this page load, which is right for a refusal and wrong for a route change: the caller says which.
 */
export function haltReplayRecorder(instance: ReplayRecorderHandle, refused: boolean): void {
  if (refused) {
    try {
      instance.sessionRecording?.dispose?.({ discardBufferedEvents: true });
    } catch {
      // Stopping below still ends the recording.
    }
  }
  instance.stopSessionRecording();
  // A request the SDK already holds (a retry after a 503, a queued batch, an unload beacon) is sent
  // later by the SDK without asking about consent again, and `opt_out_capturing` does not stop it
  // (checked against posthog-js 1.441.0). Its dispatch drops everything while `__loaded` is false, so a
  // refused instance is switched off there. A refusal is final for this page load.
  if (refused) instance.__loaded = false;
}

const ADDED_SIGNAL_EVENTS = new Set(['$$heatmap', '$dead_click', '$rageclick', '$web_vitals']);
const ADDED_SCROLL_PROPERTY = /^\$prev_pageview_(?:last|max)_(?:scroll|content)/;

/**
 * `before_send` step for the main instance: drops the added signal events, and the scroll / content
 * depth the SDK would stamp on other events, unless `allowed`. Everything else passes untouched.
 */
export function withholdAddedSignals<
  T extends { event?: string; properties?: Record<string, unknown> },
>(event: T, allowed: boolean): T | null {
  if (allowed) return event;
  if (event.event && ADDED_SIGNAL_EVENTS.has(event.event)) return null;
  if (!event.properties) return event;
  const properties = Object.fromEntries(
    Object.entries(event.properties).filter(([key]) => !ADDED_SCROLL_PROPERTY.test(key))
  );
  return { ...event, properties };
}

/** Property stamped on main-instance events so a pre-consent recording can be joined to them. */
export const PRECONSENT_REPLAY_SESSION_PROPERTY = 'preconsent_replay_session_id';

/**
 * The consent storage key of the pre-consent instances. The SDK's default is `__ph_opt_in_out_<token>`,
 * shared with the main instance, so a stored refusal (or the landing's pending opt-out cookie) would
 * silently opt the recording instance out (verified against the real SDK). A name of its own is read
 * but never written: nothing ever calls opt_in/opt_out on these instances.
 */
export const PRECONSENT_CONSENT_STORAGE_NAME = '__ph_opt_in_out_preconsent_replay';

/**
 * A copy without the given keys. Used instead of `key: undefined`, which under the SDK's
 * `{...defaults, ...options}` would OVERRIDE the default rather than leave it.
 */
export function omitKeys<T extends object, K extends keyof T>(
  source: T,
  keys: readonly K[]
): Omit<T, K> {
  const copy = { ...source };
  for (const key of keys) delete copy[key];
  return copy;
}
